import { FixedStepClock, type ClockOptions, type Tick } from "./clock";

// The runtime: one fixed-step clock plus ordered update phases, driven by requestAnimationFrame in play
// and by `advance(ms)` in tests. Genre-neutral: a composition registers its systems into phases and sets
// the step rate; nothing here knows about three.js, a kernel or a game.
//
// Phase order, every frame:
//   input  once per frame, before any step (poll gamepads, latch device edges)
//   sim    once per fixed step (gameplay, physics, AI: everything that must be deterministic)
//   late   once per frame, after the steps (interpolated transforms, animation, IK, camera)
//   render once per frame, last (draw)
// Within a phase, handlers run by `order` (lower first), then by registration order.

export type FramePhase = "input" | "late" | "render";
export type Phase = FramePhase | "sim";

/** Passed to `sim` handlers: one fixed step. */
export interface StepContext {
  /** The fixed step, seconds. */
  dt: number;
  /** Sim time at the start of this step (step index × dt). */
  time: number;
  /** This step's index. */
  step: number;
}

/** Passed to `input`, `late` and `render` handlers: one frame. */
export interface FrameContext extends Tick {
  /** Presentation dt for view-side animation: the tick's `viewDt` (real seconds × the time scale and slow-mo; in the
   *  clock's "freeze" hit-stop mode it keeps running through a hit-stop, in "scale" mode it slows with it). */
  dt: number;
  /** Sim time after this frame's steps. */
  time: number;
  /** Frames drawn so far (counts this one). */
  frame: number;
  /** "play" from the rAF loop, "advance" from a deterministic `advance(ms)` (alpha is then 1: the newest state). */
  mode: "play" | "advance";
  /** Whether the render phase runs after this late phase (false for `advance(ms, { draw: false })`: pose only). */
  draw: boolean;
}

export interface HandlerOptions {
  /** Lower runs first within the phase. Default 0. */
  order?: number;
  /** A label for `describe()`. */
  name?: string;
}

type Handler<C> = { fn: (c: C) => void; order: number; seq: number; name: string; ms: number; calls: number };

/** One handler's time while profiling ({@link Runtime.profile}): CPU ms and calls since profiling started. */
export interface HandlerTiming {
  name: string;
  ms: number;
  calls: number;
}

export interface RuntimeOptions extends ClockOptions {
  /** While paused, keep running the input/late/render phases each frame (cozy: the page stays live). The
   *  spike sets false: a paused frame does nothing, so only `advance` draws. Default true. */
  pausedRenders?: boolean;
  /** The CPU timer for the per-step cost in `stats()` (ms). Default `performance.now`; tests pass a fake. */
  cpuNow?: () => number;
}

/** How many recent steps the sim CPU statistics cover (5 s at 120 Hz). */
export const SIM_CPU_WINDOW = 600;

export interface FrameStats {
  /** Frames per second over the last ~1 s window. */
  fps: number;
  /** rAF frames seen (including paused ones). */
  frames: number;
  /** Steps in the most recent frame. */
  lastSteps: number;
  /** Steps dropped by the per-frame cap since start. */
  droppedSteps: number;
  /** Times the render phase ran (play frames plus drawing advances). */
  drawn: number;
  /** CPU time of one sim step (every `sim` handler), ms, over the last SIM_CPU_WINDOW steps: mean, p95, max. */
  simStepMs: number;
  simStepP95Ms: number;
  simStepMaxMs: number;
  /** Steps the three figures above cover. */
  simStepSamples: number;
}

export class Runtime {
  readonly clock: FixedStepClock;
  readonly pausedRenders: boolean;
  private handlers: { [P in Phase]: Handler<P extends "sim" ? StepContext : FrameContext>[] } = { input: [], sim: [], late: [], render: [] };
  private seq = 0;
  private frames = 0;
  private drawn = 0;
  private lastSteps = 0;
  private fps = 0;
  private fpsWindow = { t: 0, frames: 0 };
  private last = 0;
  private rafId: number | null = null;
  private cancel: ((id: number) => void) | null = null;
  private readonly cpuNow: () => number;
  private readonly stepCpu = new Float64Array(SIM_CPU_WINDOW);
  private stepCpuN = 0;
  private profiling = false;

  constructor(o: RuntimeOptions = {}) {
    this.clock = new FixedStepClock(o);
    this.pausedRenders = o.pausedRenders ?? true;
    this.cpuNow = o.cpuNow ?? (typeof performance !== "undefined" ? () => performance.now() : () => Date.now());
  }

  /** Register a handler; returns its remover. */
  on(phase: "sim", fn: (c: StepContext) => void, o?: HandlerOptions): () => void;
  on(phase: FramePhase, fn: (c: FrameContext) => void, o?: HandlerOptions): () => void;
  on(phase: Phase, fn: (c: never) => void, o: HandlerOptions = {}): () => void {
    const list = this.handlers[phase] as Handler<unknown>[];
    const h: Handler<unknown> = { fn: fn as (c: unknown) => void, order: o.order ?? 0, seq: this.seq++, name: o.name ?? `${phase}#${this.seq}`, ms: 0, calls: 0 };
    list.push(h);
    list.sort((a, b) => a.order - b.order || a.seq - b.seq);
    return () => {
      const i = list.indexOf(h);
      if (i >= 0) list.splice(i, 1);
    };
  }

  /** Time every handler with the CPU timer (a dev profiler), or stop. Starting resets the times. Off by default: a
   *  frame not profiled reads no clock per handler. */
  profile(on: boolean): void {
    if (on && !this.profiling) for (const p of Object.values(this.handlers)) for (const h of p as Handler<unknown>[]) (h.ms = 0), (h.calls = 0);
    this.profiling = on;
  }

  /** Each handler's CPU ms and calls since `profile(true)`, per phase in run order (zeros when never profiled). */
  timings(): Record<Phase, HandlerTiming[]> {
    const of = (p: Phase): HandlerTiming[] => (this.handlers[p] as Handler<unknown>[]).map((h) => ({ name: h.name, ms: h.ms, calls: h.calls }));
    return { input: of("input"), sim: of("sim"), late: of("late"), render: of("render") };
  }

  /** Handler names per phase, in run order (for the harness and debugging). */
  describe(): Record<Phase, string[]> {
    const names = (p: Phase): string[] => (this.handlers[p] as Handler<unknown>[]).map((h) => h.name);
    return { input: names("input"), sim: names("sim"), late: names("late"), render: names("render") };
  }

  get paused(): boolean {
    return this.clock.paused;
  }

  set paused(on: boolean) {
    this.clock.paused = on;
  }

  /** Sim time, seconds (step × dt). */
  get time(): number {
    return this.clock.time;
  }

  stats(): FrameStats {
    const n = Math.min(this.stepCpuN, SIM_CPU_WINDOW);
    const recent = Array.from(this.stepCpu.subarray(0, n)).sort((a, b) => a - b);
    const mean = n ? recent.reduce((s, v) => s + v, 0) / n : 0;
    return {
      fps: this.fps,
      frames: this.frames,
      lastSteps: this.lastSteps,
      droppedSteps: this.clock.droppedSteps,
      drawn: this.drawn,
      simStepMs: mean,
      simStepP95Ms: n ? recent[Math.min(n - 1, Math.floor(n * 0.95))]! : 0,
      simStepMaxMs: n ? recent[n - 1]! : 0,
      simStepSamples: n,
    };
  }

  /** One rendered frame of `realDt` seconds (the rAF driver calls this; tests may too). `draw: false` skips the render
   *  phase: a scripted run of many frames that reads the view's numbers, not its pixels. */
  frame(realDt: number, o: { draw?: boolean } = {}): Tick {
    const draw = o.draw !== false;
    this.frames++;
    if (this.clock.paused && !this.pausedRenders) {
      this.lastSteps = 0;
      return { realDt, scaledDt: 0, viewDt: 0, steps: 0, simDt: 0, alpha: this.clock.alpha, dropped: 0 };
    }
    const clamped = Math.min(Math.max(0, realDt), this.clock.maxFrameDt);
    // the input phase sees the frame before its steps (latch edges, poll pads)
    this.runFrame("input", this.ctx({ realDt: clamped, scaledDt: clamped * this.clock.effectiveScale, viewDt: clamped * this.clock.viewScale, steps: 0, simDt: 0, alpha: this.clock.alpha, dropped: 0 }, "play", draw));
    const tick = this.clock.frame(clamped, this.stepFn);
    this.lastSteps = tick.steps;
    const c = this.ctx(tick, "play", draw);
    if (draw) {
      this.drawn++;
      c.frame = this.drawn;
    }
    this.runFrame("late", c);
    if (draw) this.runFrame("render", c);
    return tick;
  }

  /**
   * Deterministic: advance the sim by `ms` of real time (through the time scale and hit-stop, like play),
   * then run `late` with alpha 1 (the newest state) and, unless `draw` is false, `render`. Works while paused.
   */
  advance(ms: number, o: { draw?: boolean } = {}): Tick {
    const draw = o.draw !== false;
    this.runFrame("input", this.ctx({ realDt: ms / 1000, scaledDt: 0, viewDt: 0, steps: 0, simDt: 0, alpha: 1, dropped: 0 }, "advance", draw));
    const tick = this.clock.advance(ms, this.stepFn);
    const c = this.ctx({ ...tick, alpha: 1 }, "advance", draw);
    this.runFrame("late", c);
    if (draw) {
      this.drawn++;
      c.frame = this.drawn;
      this.runFrame("render", c);
    }
    return tick;
  }

  /** Drive frames from requestAnimationFrame (or any scheduler with the same shape). */
  start(
    raf: (cb: (now: number) => void) => number = (cb) => requestAnimationFrame(cb),
    cancel: (id: number) => void = (id) => cancelAnimationFrame(id),
    now: () => number = () => performance.now(),
  ): void {
    if (this.rafId !== null) return;
    this.cancel = cancel;
    this.last = now();
    this.fpsWindow = { t: this.last, frames: 0 };
    const loop = (t: number): void => {
      this.rafId = raf(loop);
      const dt = (t - this.last) / 1000;
      this.last = t;
      this.fpsWindow.frames++;
      if (t - this.fpsWindow.t > 1000) {
        this.fps = (this.fpsWindow.frames * 1000) / (t - this.fpsWindow.t);
        this.fpsWindow = { t, frames: 0 };
      }
      this.frame(dt);
    };
    this.rafId = raf(loop);
  }

  stop(): void {
    if (this.rafId !== null) this.cancel?.(this.rafId);
    this.rafId = null;
  }

  private stepFn = (dt: number, time: number): void => {
    const c: StepContext = { dt, time, step: this.clock.step };
    const t0 = this.cpuNow();
    if (this.profiling) this.timed(this.handlers.sim, c);
    else for (const h of this.handlers.sim) h.fn(c);
    this.stepCpu[this.stepCpuN++ % SIM_CPU_WINDOW] = this.cpuNow() - t0;
  };

  private ctx(t: Tick, mode: FrameContext["mode"], draw: boolean): FrameContext {
    return { ...t, dt: t.viewDt, time: this.clock.time, frame: this.drawn, mode, draw };
  }

  private runFrame(phase: FramePhase, c: FrameContext): void {
    if (this.profiling) this.timed(this.handlers[phase], c);
    else for (const h of this.handlers[phase]) h.fn(c);
  }

  private timed<C>(list: Handler<C>[], c: C): void {
    for (const h of list) {
      const t0 = this.cpuNow();
      try {
        h.fn(c);
      } finally {
        h.ms += this.cpuNow() - t0;
        h.calls++;
      }
    }
  }
}

export function createRuntime(o: RuntimeOptions = {}): Runtime {
  return new Runtime(o);
}
